import produce from 'immer';
import React, { PropsWithChildren, PureComponent } from 'react';
import {
  getPractitionerByEmail,
  getPractitionerById,
} from '../../../apis/practitioner';
import { getPractitionerRolesByPractitionerId } from '../../../apis/practitionerRole';
import { PractitionerContext } from '../../../contexts/practitioner';
import { IPractitioner } from '../../../modules/unsw-fhir/Practitioner';
import { IPractitionerRole } from '../../../modules/unsw-fhir/PractitionerRole';
import {
  getDisplayName,
  getEmail,
  getFirstName,
  getGender,
  getLastName,
  isActive,
} from '../../../selectors/practitioner';
import {
  getAllRoles,
  getAllRolesByOrganizationId,
} from '../../../selectors/practitionerRole';

type PractitionerProviderProps = PropsWithChildren<{
  practitionerId?: string | undefined | null;
  practitionerEmail?: string | undefined | null;
}>;

class PractitionerProvider extends PureComponent<
  PractitionerProviderProps,
  PractitionerContext & {
    practitionerId?: string | undefined | null;
    practitionerEmail?: string | undefined | null;
  }
> {
  constructor(props: PractitionerProviderProps) {
    super(props);
    this.state = {
      loading: false,
      practitionerId: undefined,
      getEmail: this.getEmail,
      isActive: this.isActive,
      getGender: this.getGender,
      getRoles: this.getRoles,
      getFirstName: this.getFirstName,
      getLastName: this.getLastName,
      getDisplayName: this.getDisplayName,
      setPractitioner: this.setPractitioner,
      setPractitionerRoles: this.setPractitionerRoles,
      practitioner: undefined,
      practitionerRoles: [],
    };
  }

  componentDidUpdate = async (pProps: PractitionerProviderProps) => {
    if (
      this.props.practitionerId &&
      pProps.practitionerId !== this.props.practitionerId
    ) {
      return await this.updatePractitionerById(this.props.practitionerId);
    }

    if (
      this.props.practitionerEmail &&
      pProps.practitionerEmail !== this.props.practitionerEmail
    ) {
      return await this.updatePractitionerByEmail(this.props.practitionerEmail);
    }
  };

  componentDidMount = async () => {
    if (this.props.practitionerId) {
      return await this.updatePractitionerById(this.props.practitionerId);
    }
    if (this.props.practitionerEmail) {
      return await this.updatePractitionerByEmail(this.props.practitionerEmail);
    }
  };

  render = () => (
    <PractitionerContext.Provider value={this.state}>
      {this.props.children}
    </PractitionerContext.Provider>
  );

  private getRoles = (organizationId?: string) =>
    organizationId
      ? getAllRolesByOrganizationId(
          this.state.practitionerRoles,
          organizationId,
        )
      : getAllRoles(this.state.practitionerRoles);

  private getEmail = () => getEmail(this.state.practitioner);

  private getDisplayName = () => getDisplayName(this.state.practitioner);

  private getFirstName = () => getFirstName(this.state.practitioner);

  private getLastName = () => getLastName(this.state.practitioner);

  private getGender = () => getGender(this.state.practitioner);

  private isActive = () => isActive(this.state.practitioner);

  private setPractitioner = (practitioner: IPractitioner | undefined) =>
    this.setState(state =>
      produce(state, draft => {
        draft.practitionerId = practitioner ? practitioner.id || null : null;
        draft.practitioner = practitioner;
        return draft;
      }),
    );

  private setPractitionerRoles = (
    practitionerRoles: IPractitionerRole[] | undefined,
  ) =>
    this.setState(state =>
      produce(state, draft => {
        draft.practitionerRoles = practitionerRoles;
        return draft;
      }),
    );

  private updatePractitionerById = async (practitionerId: string) => {
    try {
      this.setState(state =>
        produce(state, draft => {
          draft.loading = true;
          return draft;
        }),
      );
      const practitioner = await getPractitionerById(practitionerId);

      const practitionerRoles = await getPractitionerRolesByPractitionerId(
        practitionerId,
      );

      this.setState(state =>
        produce(state, draft => {
          draft.practitionerId = practitionerId;
          draft.practitioner = practitioner;
          draft.practitionerRoles = practitionerRoles.data;
          draft.loading = false;
          return draft;
        }),
      );
    } catch (e) {
      this.setState(state =>
        produce(state, draft => {
          draft.loading = false;
          draft.error = e as string | undefined;
          return draft;
        }),
      );
    }
  };
  private updatePractitionerByEmail = async (email: string) => {
    try {
      this.setState(state =>
        produce(state, draft => {
          draft.loading = true;
          return draft;
        }),
      );
      const practitioner = await getPractitionerByEmail(email);
      const practitionerId = practitioner && practitioner.id;
      const practitionerRoles =
        practitionerId &&
        (await getPractitionerRolesByPractitionerId(practitionerId));

      this.setState(state =>
        produce(state, draft => {
          draft.practitionerId = practitionerId;
          draft.practitioner = practitioner;
          draft.practitionerRoles = practitionerRoles
            ? practitionerRoles.data
            : [];
          draft.loading = false;
          return draft;
        }),
      );
    } catch (e) {
      this.setState(state =>
        produce(state, draft => {
          draft.loading = false;
          draft.error = e as string | undefined;
          return draft;
        }),
      );
    }
  };
}

export default PractitionerProvider;
